import React, { useEffect, useState, useRef } from "react";
import {
  Col,
  Label,
  FormFeedback,
  Button,
  Spinner,
  Input,
  Offcanvas,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import {
  createCandidateCustomView,
  fetchCandidateCustomViewById,
  editCandidateCustomViewById,
  deleteCandidateCustomView,
  unselectCandidateCustomView,
  selectCandidateCustomView,
  setCandidateDefaultCustomView,
  deleteCandidateDefaultCustomView,
} from "../../../store/candidate/action";
import DualListBox from "react-dual-listbox";
import { initialValues, schema } from "./constants";
import {
  TableFilter,
  ArrayHelper,
  LeftSideBarCustomViewCanvas,
} from "@workspace/common";
import {
  CANDIDATE_MANDATORY_OPTIONS,
  generateCandidateFields,
} from "../../CandidateListing/candidateListingConstants";

function CandidateCustomViewCanvas({
  isFilterOpen,
  setIsFilterOpen,
  candidateCustomView,
  setCandidateCustomView,
  title = "Custom View",
  setCandidateBubbleFilter,
}) {
  document.title = "Candidate Custom View | RTS";
  const dispatch = useDispatch();
  const filterRef = useRef(null);
  const [initialValuesState, setInitialValuesState] = useState(initialValues);
  const [editId, setEditId] = useState(null);

  const allCandidateCustomViews = useSelector(
    (state) => state?.CandidateReducer?.candidateCustomViews
  );

  const candidateCustomViewData = useSelector(
    (state) => state?.CandidateReducer?.candidateCustomView
  );

  const candidateCustomViewMeta = useSelector(
    (state) => state?.CandidateReducer?.candidateCustomViewMeta
  );

  const candidateDefaultCustomView = useSelector(
    (state) => state?.CandidateReducer?.candidateDefaultCustomView
  );

  // Dispatch and get candidate_candidate form
  const [selectedOption, setSelectedOption] = useState([]);
  const [options, setOptions] = useState([]);
  const [filters, setFilters] = useState([]);

  const candidateForm = useSelector((state) => state.CandidateFormReducer.form);

  // Set the editId of selected custom view
  useEffect(() => {
    if (allCandidateCustomViews != null && allCandidateCustomViews.length > 0) {
      const selectedView = allCandidateCustomViews.find(
        (view) => view?.selected === true
      );
      if (selectedView) {
        setEditId(selectedView?.id);
      }
    }
  }, [allCandidateCustomViews]);

  useEffect(() => {
    if (editId) {
      dispatch(fetchCandidateCustomViewById(editId));
    }
  }, [editId]);

  const areOptionsEmpty = () => {
    return !(options && options.length > 0);
  };

  useEffect(() => {
    if (generateCandidateFields(candidateForm)?.length > 0) {
      const filteredOptions = generateCandidateFields(candidateForm)?.filter(
        (field) =>
          !CANDIDATE_MANDATORY_OPTIONS.some((option) =>
            field?.value.includes(option.value)
          )
      );
      ArrayHelper.sortArrayObjIcase(filteredOptions, "label");
      setOptions(filteredOptions);
    }
  }, [candidateForm]);

  const handleSubmit = async (values, flag, isApply = false) => {
    if (!flag) return;
    try {
      const newCustomView = {
        name: values.name,
        type: "Candidate",
        columnName: values.columnName || [],
        filters: filters,
      };

      if (!isApply) {
        if (editId) {
          dispatch(
            editCandidateCustomViewById({
              editId,
              payload: newCustomView,
              closeFilter: setIsFilterOpen,
            })
          );
          setCandidateBubbleFilter(null);
        } else {
          dispatch(
            createCandidateCustomView({
              payload: newCustomView,
              closeFilter: setIsFilterOpen,
            })
          );
          setCandidateBubbleFilter(null);
        }
      } else {
        dispatch(setCandidateDefaultCustomView(newCustomView));
        setCandidateBubbleFilter(null);
        setIsFilterOpen(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (candidateCustomViewData && editId) {
      setInitialValuesState({
        name: candidateCustomViewData?.name,
        columnName: candidateCustomViewData?.columnName,
      });
      setSelectedOption(candidateCustomViewData?.columnName);
      setFilters(candidateCustomViewData?.filters);
    }
    if (editId === null) {
      setInitialValuesState({
        name: candidateDefaultCustomView?.name ?? "",
        columnName: candidateDefaultCustomView?.columnName ?? [],
      });
      setSelectedOption(candidateDefaultCustomView?.columnName ?? []);
      setFilters(candidateDefaultCustomView?.filters ?? []);
    }
  }, [candidateCustomViewData, editId]);

  const formik = useFormik({
    initialValues: initialValuesState,
    validationSchema: schema,
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const flag = filterRef.current?.validate();
      handleSubmit(values, flag);
    },
  });

  const closeFilter = () => {
    setIsFilterOpen(false);
  };

  const handleDefaultViewSelection = (closeModal = true) => {
    if (candidateCustomView) {
      dispatch(unselectCandidateCustomView());
    }
    setCandidateBubbleFilter(null);
    dispatch(deleteCandidateDefaultCustomView());
    setEditId(null);
    setCandidateCustomView(null);
    if (closeModal) {
      closeFilter();
    }
  };

  const handleSelectCustomView = (id) => {
    dispatch(deleteCandidateDefaultCustomView());
    setCandidateCustomView(null);
    dispatch(selectCandidateCustomView({ id: id }));
    setCandidateBubbleFilter(null);
    closeFilter();
  };

  const handleClearCustomView = () => {
    setInitialValuesState(initialValues);
    setSelectedOption([]);
    setFilters([]);
    setCandidateBubbleFilter(null);
    formik.resetForm();
  };

  const handleDeleteCustomView = (id) => {
    dispatch(deleteCandidateCustomView({ id: id }));
  };

  const transformOptions = (options, removeNull) => {
    if (!options) return [];
    if (removeNull) {
      return options
        .filter((opt) => opt?.sortValue != null)
        .map((opt) => {
          return {
            label: opt?.label,
            value: opt?.sortValue,
            filterType: opt?.filterType,
            dbDataType: opt?.dbDataType,
            externalFilter: opt?.externalFilter,
            element: opt?.element,
          };
        });
    }
    return options.map((opt) => {
      return {
        label: opt?.label,
        value: opt?.sortValue,
        filterType: opt?.filterType,
        dbDataType: opt?.dbDataType,
        externalFilter: opt?.externalFilter,
        element: opt?.element,
      };
    });
  };

  return (
    <Offcanvas
      isOpen={isFilterOpen}
      toggle={() => setIsFilterOpen(!isFilterOpen)}
      direction="end"
      style={{ width: "90vw", borderLeft: "1px solid #d3d3d3" }}
    >
      <div
        className="offcanvas-header"
        style={{ borderBottom: "1px solid #d3d3d3" }}
      >
        <span className="fs-5 fw-semibold">{title}</span>
        <span
          className="ri-close-fill fs-4 cursor-pointer"
          onClick={() => closeFilter()}
        ></span>
      </div>
      <div className="offcanvas-body p-1">
        <div className="d-flex flex-row gap-2 h-100">
          {/* 1 */}
          <div style={{ width: "15%" }}>
            <LeftSideBarCustomViewCanvas
              customViews={allCandidateCustomViews}
              setEditId={setEditId}
              editId={editId}
              handleDefaultViewSelection={handleDefaultViewSelection}
              handleSelectCustomView={handleSelectCustomView}
              handleDeleteCustomView={handleDeleteCustomView}
            />
          </div>
          {/* Separator */}
          <div
            style={{
              width: "1px",
              minHeight: "100%",
              backgroundColor: "#DCE0E5",
            }}
          ></div>
          {/* 2 */}
          <div style={{ width: "50%" }}>
            <div className="mb-3">
              <div className="px-2"></div>
              <div>
                <Label className="fw-semibold">Custom View Name*</Label>
                <Input
                  name="name"
                  type="text"
                  placeholder="Enter Custom View Name"
                  className={`form-control ${
                    formik.errors.name && formik.touched.name && "is-invalid"
                  }`}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.errors.name && formik.touched.name && (
                  <FormFeedback typeof="invalid">
                    {formik.errors.name}
                  </FormFeedback>
                )}
              </div>
            </div>
            <div>
              {/* Filter Element */}
              <TableFilter
                fields={transformOptions(
                  generateCandidateFields(candidateForm),
                  true
                )}
                filters={filters}
                setFilters={setFilters}
                ref={filterRef}
                height="270px"
                formSchema={[
                  {
                    prefix: "candidate_submission_data.",
                    schemas: candidateForm?.formSchema,
                  },
                ]}
              />
            </div>
          </div>
          {/* Separator */}
          <div
            style={{
              width: "1px",
              minHeight: "100%",
              backgroundColor: "#DCE0E5",
            }}
          ></div>
          {/* 3 */}
          <div
            style={{ width: "30%" }}
            className="d-flex justify-content-start ps-2"
          >
            <div className="mb-3">
              <div className="d-flex flex-column pt-2">
                <Label className="fw-semibold">Custom View Columns*</Label>
              </div>
              <Row>
                <Col>
                  {formik.errors.columnName && (
                    <div className="mt-1">
                      <p className="text-danger">{formik.errors.columnName}</p>
                    </div>
                  )}
                </Col>
              </Row>
              <Row className="mb-1">
                <Col lg="6" className="pe-0">
                  <span className="fw-semibold">Available</span>
                </Col>
                <Col lg="6" className="px-0">
                  <span className="fw-semibold">Selected</span>
                </Col>
              </Row>

              <DualListBox
                className="h-75"
                options={options ?? []}
                selected={selectedOption}
                onChange={(newValue) => {
                  setSelectedOption(newValue);
                  formik.setFieldValue("columnName", newValue);
                }}
                showOrderButtons
                preserveSelectOrder
                icons={{
                  moveLeft: [
                    <span
                      className={`mdi mdi-chevron-left ${
                        areOptionsEmpty() ? "disabled-icon" : ""
                      }`}
                      key="key"
                    />,
                  ],
                  moveAllLeft: [
                    <span
                      className={`mdi mdi-chevron-double-left ${
                        areOptionsEmpty() ? "disabled-icon" : ""
                      }`}
                      key="key"
                    />,
                  ],
                  moveRight: (
                    <span
                      className={`mdi mdi-chevron-right ${
                        areOptionsEmpty() ? "disabled-icon" : ""
                      }`}
                      key="key"
                    />
                  ),
                  moveAllRight: [
                    <span
                      className={`mdi mdi-chevron-double-right ${
                        areOptionsEmpty() ? "disabled-icon cursor-none" : ""
                      }`}
                      key="key"
                    />,
                  ],
                  moveDown: <span className="mdi mdi-chevron-down" />,
                  moveUp: <span className="mdi mdi-chevron-up" />,
                  moveTop: <span className="mdi mdi-chevron-double-up" />,
                  moveBottom: <span className="mdi mdi-chevron-double-down" />,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="offcanvas-footer  d-flex flex-row justify-content-between p-3"
        style={{ borderTop: "1px solid #d3d3d3" }}
      >
        <div>
          <Button
            className="cv-footer-cancel"
            onClick={() => setIsFilterOpen(false)}
          >
            Cancel
          </Button>
        </div>
        <div className="d-flex flex-row gap-2">
          <Button className="cv-footer-clear" onClick={handleClearCustomView}>
            Clear
          </Button>
          {(!candidateCustomView || candidateCustomView == -1) && (
            <Button
              type="button"
              className="cv-footer-apply"
              onClick={async () => {
                const formikErrors = await formik.validateForm();
                formik.setTouched({ name: true });
                const hasFormikErrors = Object.keys(formikErrors).length > 0;
                const flag = filterRef.current?.validate();

                if (!hasFormikErrors && flag) {
                  handleSubmit(formik.values, flag, true);
                }
                formik.setSubmitting(false);
              }}
            >
              Apply
            </Button>
          )}
          <Button
            className="cv-footer-save"
            onClick={async () => {
              const formikErrors = await formik.validateForm();
              formik.setTouched({ name: true });
              const hasFormikErrors = Object.keys(formikErrors).length > 0;
              const flag = filterRef.current?.validate();

              if (!hasFormikErrors && flag) {
                handleSubmit(formik.values, flag, false);
              }
              formik.setSubmitting(false);
            }}
          >
            {candidateCustomViewMeta?.isLoading ? (
              <Spinner size="sm" color="light" />
            ) : editId ? (
              "Apply & Update"
            ) : (
              "Apply & Save"
            )}
          </Button>
        </div>
      </div>
    </Offcanvas>
  );
}

export default CandidateCustomViewCanvas;
